import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

export interface AuthSessionPayload {
    token?: string;
    groups?: unknown;
    is_superadmin?: string | number | boolean | null;
    is_staff?: string | number | boolean | null;
    user_id?: string | number | null;
}

interface AuthContextType {
    isAuthenticated: boolean;
    login: (credentials: { email: string; password: string }) => Promise<AuthSessionPayload>;
    applyAuthSession: (payload: AuthSessionPayload) => void;
    logout: () => Promise<void>;
    loading: boolean;
    authError: Error | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [authError, setAuthError] = useState<Error | null>(null);

    useEffect(() => {
        setIsAuthenticated(!!localStorage.getItem('user_id'));
        setLoading(false);

        const onSessionExpired = () => {
            localStorage.removeItem('groups');
            localStorage.removeItem('is_superadmin');
            localStorage.removeItem('is_staff');
            localStorage.removeItem('user_id');
            setIsAuthenticated(false);
        };
        window.addEventListener('auth:session-expired', onSessionExpired);
        return () => window.removeEventListener('auth:session-expired', onSessionExpired);
    }, []);

    const applyAuthSession = (payload: AuthSessionPayload) => {
        if (payload.groups !== undefined) {
            localStorage.setItem('groups', JSON.stringify(payload.groups));
        }
        if (payload.is_superadmin !== undefined && payload.is_superadmin !== null) {
            localStorage.setItem('is_superadmin', String(payload.is_superadmin));
        }
        if (payload.is_staff !== undefined && payload.is_staff !== null) {
            localStorage.setItem('is_staff', String(payload.is_staff));
        }
        if (payload.user_id !== undefined && payload.user_id !== null) {
            localStorage.setItem('user_id', String(payload.user_id));
        }
        setIsAuthenticated(true);
        setAuthError(null);
    };

    const login = async (credentials: { email: string; password: string }): Promise<AuthSessionPayload> => {
        try {
            const response = await fetch('/api/token/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(credentials),
            });
            if (!response.ok) {
                throw new Error('Login fehlgeschlagen');
            }
            const data = await response.json();
            applyAuthSession(data as AuthSessionPayload);
            return data;
        } catch (error: unknown) {
            setAuthError(error instanceof Error ? error : new Error('Login fehlgeschlagen'));
            throw error;
        }
    };

    const logout = async () => {
        try {
            await fetch('/api/logout/', {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            // Ignore network errors during logout
        }
        localStorage.removeItem('groups');
        localStorage.removeItem('is_superadmin');
        localStorage.removeItem('is_staff');
        localStorage.removeItem('user_id');
        setIsAuthenticated(false);
        setAuthError(null);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, applyAuthSession, logout, loading, authError }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
